import type { UseQueryResult, UseMutationResult } from 'react-query';
import { useQuery, useMutation } from 'react-query';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import { invalidateFeedbackSummaryCache } from '@hotjar/webapp/hooks/useFeedbackSummary';
import type { FeedbackResponseFilters } from '@hotjar/feedback/components/IncomingFeedback/ResponsesFilters/ResponsesFilters';
import { type Site } from '@hotjar/site';
import { type FeedbackWidgetIdByUrlResult, getFeedbackResponse } from './api';
import {
  deleteFeedbackResponse,
  deleteFeedbackWidget,
  getFeedbackWidgets,
  getFeedbackWidget,
  toggleWidgetStatus,
  saveFeedbackWidget,
  getFeedbackWidgetIdByUrl,
  getFeedbackWidgetResults,
} from './api';
import type { Feedback } from './Feedback';
import { invalidateFeedbackToolsCount } from '../common/query';
import { mapFiltersToDto } from './filters/FiltersMappings';
import { DEFAULT_DATE_FILTER } from './filters/ResponsesFilters';
import type { FeedbackResultsDto } from './FeedbackResultsDto';
import { type FeedbackResponseDto } from './FeedbackResponseDto';

const FEEDBACK_QUERY_KEY = 'feedback';
const FEEDBACK_STALE_TIME = 1000 * 60 * 5;

export const invalidateFeedbackWidgets = (siteId: number): void => {
  queryClient.invalidateQueries([FEEDBACK_QUERY_KEY, { siteId }]);
};

export const invalidateFeedbackWidget = (siteId: number, widgetId: number): void => {
  queryClient.invalidateQueries([FEEDBACK_QUERY_KEY, { siteId }]);
  queryClient.invalidateQueries([FEEDBACK_QUERY_KEY, { siteId, widgetId }]);
};

export const useFeedbackWidgetsQuery = (site?: Site): UseQueryResult<Feedback[], Error> => {
  const hasSiteId = typeof site?.id !== 'undefined';

  return useQuery(
    [FEEDBACK_QUERY_KEY, { siteId: site?.id }],
    () => {
      if (site === undefined) throw new Error();
      return getFeedbackWidgets(site);
    },
    {
      enabled: hasSiteId,
      staleTime: FEEDBACK_STALE_TIME,
    },
  );
};

export const useFeedbackWidgetQuery = (
  site?: Site,
  widgetId?: number,
): UseQueryResult<Feedback, Error> => {
  return useQuery(
    [FEEDBACK_QUERY_KEY, { siteId: site?.id, widgetId }],
    () => {
      if (site === undefined || widgetId === undefined) throw new Error();
      return getFeedbackWidget(site, widgetId);
    },
    {
      enabled: Boolean(site?.id) && Boolean(widgetId),
      staleTime: FEEDBACK_STALE_TIME,
    },
  );
};

type DeleteFeedbackWidgetVariables = { site: Site; feedbackWidgetId: number };

export const useDeleteFeedbackWidgetMutation = (): UseMutationResult<
  void,
  unknown,
  DeleteFeedbackWidgetVariables,
  unknown
> => {
  return useMutation(({ site, feedbackWidgetId }) => deleteFeedbackWidget(site, feedbackWidgetId), {
    onSuccess: (_, { site, feedbackWidgetId }) => {
      invalidateFeedbackSummaryCache();
      invalidateFeedbackWidget(site.id, feedbackWidgetId);
      invalidateFeedbackToolsCount(site.id, 'feedback');
    },
  });
};

type DeleteFeedbackResponseVariables = {
  site: Site;
  feedbackWidgetId: number;
  responseId: number;
};

export const useDeleteFeedbackResponseMutation = (): UseMutationResult<
  void,
  unknown,
  DeleteFeedbackResponseVariables,
  unknown
> => {
  return useMutation(
    ({ site, feedbackWidgetId, responseId }) =>
      deleteFeedbackResponse(site, feedbackWidgetId, responseId),
    {
      onSuccess: (_, { site, feedbackWidgetId }) => {
        invalidateFeedbackSummaryCache();
        invalidateFeedbackWidget(site.id, feedbackWidgetId);
      },
    },
  );
};

type ToggleFeedbackWidgetStatusVariables = { site: Site; feedbackWidgetId: number };

export const useToggleFeedbackWidgetStatusMutation = (): UseMutationResult<
  void,
  unknown,
  ToggleFeedbackWidgetStatusVariables,
  unknown
> => {
  return useMutation(({ site, feedbackWidgetId }) => toggleWidgetStatus(site, feedbackWidgetId), {
    onSuccess: (_, { site, feedbackWidgetId }) => {
      invalidateFeedbackSummaryCache();
      invalidateFeedbackWidget(site.id, feedbackWidgetId);
    },
  });
};

type RenameFeedbackWidgetVariables = {
  site: Site;
  feedbackWidgetId: number;
  name: string;
};

export const useRenameFeedbackWidgetMutation = (): UseMutationResult<
  void,
  unknown,
  RenameFeedbackWidgetVariables,
  unknown
> => {
  return useMutation(
    async ({ site, feedbackWidgetId, name }) => {
      // It would be better if we had a dedicated rename endpoint but for now we can request a fresh copy of the widget
      const widget = await getFeedbackWidget(site, feedbackWidgetId);
      await saveFeedbackWidget(site, { ...widget, name });
    },
    {
      onSuccess: (_, { site, feedbackWidgetId }) => {
        invalidateFeedbackSummaryCache();
        invalidateFeedbackWidget(site.id, feedbackWidgetId);
      },
    },
  );
};

export const useFindFeedbackWidgetIdQuery = (
  site: Site,
  filters: Partial<FeedbackResponseFilters>,
): UseQueryResult<FeedbackWidgetIdByUrlResult, Error> => {
  const clauses = mapFiltersToDto(filters);
  return useQuery(
    [FEEDBACK_QUERY_KEY, { siteId: site.id, clauses }],
    () => {
      if (site === undefined) throw new Error();

      return getFeedbackWidgetIdByUrl(site, clauses);
    },
    {
      enabled: clauses.length > 0,
      staleTime: FEEDBACK_STALE_TIME,
      refetchOnWindowFocus: false,
      // Disabling rety until RAN-230 is deployed
      retry: false,
    },
  );
};

export const useFeedbackWidgetResults = (
  site?: Site,
  widgetId?: number,
  filters?: Partial<FeedbackResponseFilters>,
): UseQueryResult<FeedbackResultsDto, Error> => {
  const clauses = mapFiltersToDto(filters || { date: DEFAULT_DATE_FILTER });

  return useQuery(
    [FEEDBACK_QUERY_KEY, { siteId: site?.id, widgetId, clauses }],
    () => {
      if (site === undefined || widgetId === undefined) throw new Error();

      return getFeedbackWidgetResults(site, widgetId, clauses);
    },
    {
      enabled: site?.id !== undefined && widgetId !== undefined,
      staleTime: FEEDBACK_STALE_TIME,
      refetchOnWindowFocus: false,
    },
  );
};

export const useFeedbackResponseQuery = (
  site: Site | undefined,
  widgetId: number | undefined,
  responseId: number | undefined,
): UseQueryResult<FeedbackResponseDto, Error> => {
  return useQuery(
    [FEEDBACK_QUERY_KEY, { siteId: site?.id, widgetId, responseId }],
    () => {
      if (site === undefined || widgetId === undefined || responseId === undefined)
        throw new Error();
      return getFeedbackResponse(site, widgetId, responseId);
    },
    {
      enabled: site !== undefined && widgetId !== undefined && responseId !== undefined,
      staleTime: FEEDBACK_STALE_TIME,
      refetchOnWindowFocus: false,
    },
  );
};

/* Imperative versions of the queries for angular */

export const queryFeedbackWidgets = (site: Site): Promise<Feedback[]> => {
  return queryClient.fetchQuery(
    [FEEDBACK_QUERY_KEY, { siteId: site.id }],
    () => {
      if (site === undefined) throw new Error();
      return getFeedbackWidgets(site);
    },
    {
      staleTime: FEEDBACK_STALE_TIME,
    },
  );
};

export async function queryDefaultFeedbackWidget(site: Site): Promise<Feedback> {
  return queryClient.fetchQuery([FEEDBACK_QUERY_KEY, { siteId: site.id }, 'default'], async () => {
    const result = await queryFeedbackWidgets(site);

    return result
      .sort((a, b) => b.created_epoch_time - a.created_epoch_time)
      .sort((a, b) => Number(b.enabled) - Number(a.enabled))[0];
  });
}

export async function mutationDeleteFeedbackResponse(
  site: Site,
  feedbackWidgetId: number,
  responseId: number,
): Promise<void> {
  await deleteFeedbackResponse(site, feedbackWidgetId, responseId);
  invalidateFeedbackSummaryCache();
  invalidateFeedbackWidget(site.id, feedbackWidgetId);
}
